/**
 * Turning an engine's raw track description into something a viewer can choose between.
 *
 * ## Why this is not just `track.name`
 *
 * A menu is only useful if its rows are distinguishable, and raw track metadata routinely
 * fails that in three separate ways:
 *
 *   - **The name is missing.** hls.js reports `name: ''` for plenty of real playlists and
 *     AVPlay's `extra_info` frequently carries a language and nothing else. A blank row is
 *     unselectable in practice — the viewer cannot tell which one they just picked.
 *   - **The name is the code.** `"tur"` as a display label is not a translation, it is the
 *     raw field leaking through. The viewer is asked to know ISO 639-2.
 *   - **Two rows come out identical.** Two English audio tracks — commonly a stereo and a
 *     5.1 mix — both label as "İngilizce", and the menu offers what looks like the same
 *     answer twice. This is the failure worth the most care, because it is the one that
 *     makes the menu actively misleading rather than merely plain: picking either row
 *     looks like it did nothing.
 *
 * So labelling is a property of the **list**, not of a track. {@link describeTracks} takes
 * all of them at once, which is the only way the third problem can be seen at all.
 */
import { toIso1 } from './trackPreference';

/**
 * Turkish display names for the languages the preference UI offers.
 *
 * Deliberately the same set as `ALIASES` in `trackPreference` and no larger. A code
 * outside it is shown uppercased as-is ("PT"), which is honest — it says the stream
 * declared a language this app has no name for, rather than inventing one or hiding it.
 */
const NAMES: Record<string, string> = {
  tr: 'Türkçe',
  en: 'İngilizce',
  de: 'Almanca',
  ar: 'Arapça',
  fr: 'Fransızca',
  es: 'İspanyolca',
  ru: 'Rusça',
};

/** A track as an engine hands it over, before it is fit to display. */
export interface RawTrack {
  /** Opaque to everything above the engine; only ever compared and passed back. */
  id: string;
  lang?: string | null;
  name?: string | null;
}

export interface LabelledTrack {
  id: string;
  label: string;
  /** ISO 639-1 where one could be established, for the preference tick. */
  lang: string | null;
}

/**
 * `true` when a track's own name adds information beyond the language name.
 *
 * "Türkçe" alongside the language Türkçe is noise; "5.1" or "Yorum" is the entire reason
 * the row exists. Compared case-insensitively via `toLowerCase`, which is locale-
 * independent in JavaScript — `toLocaleLowerCase` is the one that would fold `I` to `ı`
 * under a Turkish locale and make "IsIm" stop matching itself.
 */
function addsSomething(name: string, languageName: string | null, code: string | null): boolean {
  const n = name.trim().toLowerCase();
  if (!n) return false;
  if (code && n === code) return false;
  if (languageName && n === languageName.toLowerCase()) return false;
  return true;
}

/**
 * @param tracks The engine's list, in the engine's own order.
 * @param kind   Only used for the last-resort label, which has to say what it numbers.
 */
export function describeTracks(
  tracks: readonly RawTrack[],
  kind: 'audio' | 'subtitle',
): LabelledTrack[] {
  const fallback = kind === 'audio' ? 'Ses' : 'Altyazı';

  const described = tracks.map((track, index) => {
    const code = toIso1(track.lang);
    const languageName = code ? (NAMES[code] ?? code.toUpperCase()) : null;
    const own = (track.name ?? '').trim();

    let label: string;
    if (languageName && addsSomething(own, languageName, code)) label = `${languageName} — ${own}`;
    else if (languageName) label = languageName;
    else if (own) label = own;
    else label = `${fallback} ${index + 1}`;

    return { id: track.id, label, lang: code };
  });

  /*
   * Disambiguate collisions, and only collisions.
   *
   * Numbering every row would put a "1" on the single-track case, where there is nothing
   * to distinguish it from. The number is appended to *all* members of a colliding group,
   * including the first — labelling the second one "İngilizce 2" while the first stays
   * "İngilizce" reads as though the first is the real one and the second an afterthought.
   */
  const counts = new Map<string, number>();
  for (const t of described) counts.set(t.label, (counts.get(t.label) ?? 0) + 1);

  const seen = new Map<string, number>();
  return described.map((t) => {
    if ((counts.get(t.label) ?? 0) < 2) return t;
    const n = (seen.get(t.label) ?? 0) + 1;
    seen.set(t.label, n);
    return { ...t, label: `${t.label} ${n}` };
  });
}
